import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  Renderer2,
  PLATFORM_ID,
  inject,
  computed,
  linkedSignal,
  signal,
  effect,
  viewChild,
  DOCUMENT,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AnalyticsService, SessionAppDataService } from '@pedix-workspace/pedixapp-core-services';
import {
  AVAILABLE_LANGUAGES,
  DEFAULT_LANGUAGE,
  Establishment,
  SUPPORTED_LANGUAGES,
  UserDataWithClaims,
} from '@pedix-workspace/utils';
import { ThemeService } from '@pedix-workspace/angular-utils';
import { isPlatformBrowser, isPlatformServer, NgClass } from '@angular/common';
import { SeoService } from '../seo.service';
import { Meta } from '@angular/platform-browser';
import { DrawerComponent } from '@pedix-workspace/angular-ui-nav';
import { EstablishmentWorkingHoursService } from '../establishment-working-hours.service';
import { ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  Router,
  RouterOutlet,
} from '@angular/router';
import { IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';
import { WorkingHoursMessageComponent } from '../working-hours-message/working-hours-message.component';
import { SideMenuComponent } from '../side-menu/side-menu.component';
import { HeaderComponent } from '../header/header.component';
import { TranslocoDirective } from '@jsverse/transloco';
import { AppRequestContextService } from '../../../app-request-context.service';
import { AppCartItemsService } from '../../../app-cart-items.service';
import { filter, map } from 'rxjs';
import { PixelModule, PixelService } from 'ngx-multi-pixel';
import { environment } from '../../../../environments/environment';
import { AppPathLocationStrategy } from '../../../app.path-location-strategy';
import { AnnouncementsMessageComponent } from '../announcements-message/announcements-message.component';
import {
  CartItemsRemovedDialogComponent,
  PricingErrorsDialogComponent,
} from '@pedix-workspace/angular-shared-order';

@Component({
  selector: 'app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    NgClass,
    HeaderComponent,
    DrawerComponent,
    SideMenuComponent,
    WorkingHoursMessageComponent,
    IconLoadingComponent,
    RouterOutlet,
    PixelModule,
    AnnouncementsMessageComponent,
    IconLoadingComponent,
  ],
})
export class AppLayoutComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private renderer = inject(Renderer2);
  private meta = inject(Meta);
  private themeService = inject(ThemeService);
  private analytics = inject(AnalyticsService);
  private seoService = inject(SeoService);
  private appRequestContextService = inject(AppRequestContextService);
  private appCartItemsService = inject(AppCartItemsService);
  private establishmentWorkingHours = inject(EstablishmentWorkingHoursService);
  private pixel = inject(PixelService);
  private appPathLocationStrategy = inject(AppPathLocationStrategy);
  private modalDialogService = inject(ModalDialogService);
  private sessionAppDataService = inject(SessionAppDataService);
  private destroyRef = inject(DestroyRef);

  static firstRender = true;

  readonly drawer = viewChild.required(DrawerComponent);

  readonly sideMenuOpen = signal<boolean | null>(null);
  readonly announcementsHidden = signal(false);
  readonly searchText = signal('');
  readonly openedStatus = toSignal(this.establishmentWorkingHours.openedStatus$);
  readonly loadingLanguage = signal(false);
  readonly switchingSubsidiary = signal(false);

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  readonly isSubsidiaryLanding = computed(() => {
    return this.currentUrl().startsWith('/sucursal');
  });

  readonly loading = computed(() => this.loadingLanguage() || this.switchingSubsidiary());
  readonly establishment = computed(() => this.appRequestContextService.establishment());
  readonly selectedLanguage = linkedSignal(() => this.appRequestContextService.selectedLanguage());
  readonly selectedSubsidiaryId = linkedSignal(
    () => this.appRequestContextService.establishment().id,
  );
  readonly hasCartItems = computed(() => this.appCartItemsService.cartItems().length > 0);
  readonly subsidiarySelector = computed(() => this.appRequestContextService.subsidiarySelector());
  readonly isSubsidiaryIdFromTrustedSource = computed(() =>
    this.appRequestContextService.isSubsidiaryIdFromTrustedSource(),
  );

  readonly displayAdminButton = computed(() => {
    if (isPlatformServer(this.platformId)) {
      return false;
    }
    const establishment = this.establishment();
    const user = this.sessionAppDataService.sessionData().user;

    return (
      this.isEstablishmentOwner(establishment, user) && !this.appRequestContextService.previewMode()
    );
  });

  readonly availableLanguages = computed<AVAILABLE_LANGUAGES>(() => {
    const establishment = this.establishment();

    if (!establishment.features.includes('ALT_LANGUAGE') || !establishment.altLanguage) {
      return [DEFAULT_LANGUAGE];
    }
    return [DEFAULT_LANGUAGE, establishment.altLanguage];
  });

  readonly hasSubsidiaries = computed(() => this.establishment().subsidiaries?.length > 0);

  readonly catalogIsEnabled = computed(() => {
    const openedStatus = this.openedStatus();
    const establishment = this.establishment();

    if (openedStatus?.isTemporaryClosed && !establishment.temporaryClosure?.allowCatalogBrowsing) {
      return false;
    }
    return true;
  });

  readonly displayWorkingHoursMessage = computed(() => {
    const hasSubsidiaries = this.hasSubsidiaries();
    const isSubsidiarySelector = this.subsidiarySelector();
    const openedStatus = this.openedStatus();
    const catalogIsEnabled = this.catalogIsEnabled();

    if (hasSubsidiaries && !isSubsidiarySelector) {
      return false;
    }
    if (!openedStatus) {
      return false;
    }
    return (
      catalogIsEnabled &&
      (openedStatus.isClosed ||
        openedStatus.isAboutToClose ||
        openedStatus.isTemporaryClosed ||
        openedStatus.isInventoryBlocked)
    );
  });

  readonly hasLegalLinks = computed(() => {
    const links = this.establishment()?.links;
    return Boolean(
      links?.returnPolicy ||
      links?.privacyPolicy ||
      links?.termsOfService ||
      links?.withdrawalButton,
    );
  });

  readonly displayAnnouncements = computed(() => {
    const announcementsHidden = this.announcementsHidden();
    const establishment = this.establishment();

    if (isPlatformServer(this.platformId)) {
      return false;
    }
    if (announcementsHidden) {
      return false;
    }
    return Boolean(establishment.announcements?.title || establishment.announcements?.body);
  });

  constructor() {
    effect(() => {
      const establishment = this.establishment();
      if (establishment.theme) {
        this.themeService.configureTheme(establishment.theme);
      } else {
        this.themeService.resetTheme();
      }
    });
  }

  ngOnInit() {
    this.searchText.set(this.activatedRoute.snapshot.queryParams['q'] || '');

    this.analytics.establishmentSlug = this.establishment().slug;
    this.analytics.establishmentCurrency = this.establishment().currencyCode;
    this.analytics.establishmentId = this.establishment().id;

    if (AppLayoutComponent.firstRender) {
      AppLayoutComponent.firstRender = false;

      this.analytics.userLanded();
    }

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (
        event instanceof NavigationError &&
        (event.error.name === 'ChunkLoadError' ||
          event.error.message.includes('Failed to fetch dynamically imported module'))
      ) {
        const url = this.appPathLocationStrategy.prepareExternalUrl(event.url);

        window.location.href = `/${url}`;
      }
    });

    this.renderer.addClass(this.document.documentElement, 'ready');

    this.pixel.initialize([environment.pedixPixelId]);

    if (this.establishment().facebookPixel) {
      this.pixel.remove();
      this.pixel.initialize([this.establishment().facebookPixel, environment.pedixPixelId]);
    }

    if (this.establishment().googleAnalytics) {
      this.addPreconnectLink('gtm', 'https://www.googletagmanager.com');

      this.analytics.initializeGoogleAnalyticsTracking(this.establishment().googleAnalytics);
    }

    if (this.establishment().googleTagManager) {
      this.addPreconnectLink('gtm', 'https://www.googletagmanager.com');

      this.analytics.loadGoogleTagManagerScript(
        this.renderer,
        this.establishment().googleTagManager,
      );
    }

    if (this.establishment().facebookDomainVerification) {
      this.meta.addTag({
        name: 'facebook-domain-verification',
        content: this.establishment().facebookDomainVerification,
      });
    }

    this.seoService.setDefaultMetadata();
  }

  onClickLateralMenu($event) {
    this.sideMenuOpen.set(!this.sideMenuOpen());

    const path = $event.path || $event?.composedPath();

    if (this.sideMenuOpen() && (!path || !path.includes(document.querySelector('.home-button')))) {
      this.sideMenuOpen.set(false);
    }
  }

  onNavItemClick() {
    if (this.drawer().display === 'desktop') {
      return;
    }
    this.sideMenuOpen.set(false);
  }

  async onLanguageSelected(selectedLanguage: SUPPORTED_LANGUAGES) {
    this.selectedLanguage.set(selectedLanguage);
    this.loadingLanguage.set(true);

    await this.sessionAppDataService.setLanguage(selectedLanguage);

    setTimeout(() => {
      window.location.reload();
    });
  }

  async onSwitchSubsidiary(subsidiaryId: string) {
    if (
      !subsidiaryId ||
      (subsidiaryId === this.selectedSubsidiaryId() && this.isSubsidiaryIdFromTrustedSource())
    ) {
      return;
    }
    this.switchingSubsidiary.set(true);

    const { pricingErrors, removedCartItems } =
      await this.appRequestContextService.switchSubsidiaryCatalog({
        establishmentId: subsidiaryId,
        isUserSelected: true,
      });

    this.switchingSubsidiary.set(false);

    if (removedCartItems.length > 0) {
      this.modalDialogService
        .attachDialog(CartItemsRemovedDialogComponent)
        .open(removedCartItems, 'alert')
        .then(() => {
          this.router.navigate(['/resumen']);
        })
        .catch(() => {
          this.router.navigate(['/']);
        });
    } else {
      this.router.navigate(['/']);
    }
    if (pricingErrors.length > 0) {
      this.modalDialogService
        .attachDialog(PricingErrorsDialogComponent)
        .open(pricingErrors, 'confirm')
        .then(() => {
          this.router.navigate(['/resumen']);
        })
        .catch(() => {
          this.router.navigate(['/']);
        });
    } else {
      this.router.navigate(['/']);
    }
  }

  protected onProductSearch(searchText: string) {
    this.searchText.set(searchText);

    if (searchText) {
      this.router.navigate(['/busqueda'], { queryParams: { q: searchText } });
    } else {
      this.router.navigate(['/']);
    }
  }

  private isEstablishmentOwner(
    establishment: Pick<Establishment, 'userId'>,
    user: UserDataWithClaims | null,
  ) {
    if (!user) {
      return false;
    }
    if (user.claims.appSuperAdmin) {
      return true;
    }
    return establishment.userId === user.uid;
  }

  private addPreconnectLink(id: string, preconnectUrl: string) {
    if (isPlatformBrowser(this.platformId) || this.document.getElementById(id)) {
      return;
    }
    const preconnectLink = this.document.createElement('link');

    preconnectLink.id = id;
    preconnectLink.href = preconnectUrl;
    preconnectLink.rel = 'preconnect';

    this.renderer.appendChild(this.document.head, preconnectLink);
  }
}
